import { useCallback, useEffect, useState } from "react";
import { Activity, RefreshCw, Info } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Line, Area, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid, Legend } from "recharts";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtMoney } from "@/lib/api";
import { Panel, Spinner, Tag } from "@/components/common";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CampaignBudgetAction } from "@/components/CampaignBudgetAction";

const PILL = { on_track: ["emerald", "On track"], pacing_high: ["amber", "Pacing high"], over_cap: ["red", "Over cap"] };

const Bar = ({ value, max, testid }) => (
  <div className="h-1.5 overflow-hidden rounded bg-slate-800" data-testid={testid}>
    <div className={`h-full ${value > max ? "bg-red-400" : "bg-emerald-400"}`} style={{ width: `${Math.min(100, (value / (max || 1)) * 100)}%` }} />
  </div>
);

const Chart = ({ points }) => (
  <div className="h-64" data-testid="pacing-chart">
    <ResponsiveContainer width="100%" height="100%" minWidth={0} initialDimension={{ width: 600, height: 256 }}>
      <ComposedChart data={points} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
        <CartesianGrid stroke="#1e293b" vertical={false} />
        <XAxis dataKey="hour" tick={{ fill: "#64748b", fontSize: 10 }} interval={2} />
        <YAxis tick={{ fill: "#64748b", fontSize: 10 }} />
        <RTooltip contentStyle={{ background: "#0b1220", border: "1px solid #1e293b", fontSize: 12 }} formatter={(v) => fmtMoney(v)} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Area type="monotone" dataKey="spend" name="Spend today" stroke="#34d399" fill="#34d39933" strokeWidth={2} connectNulls={false} isAnimationActive={false} />
        <Line type="monotone" dataKey="projected" name="Projected" stroke="#38bdf8" strokeDasharray="5 4" dot={false} isAnimationActive={false} />
        <Line type="stepAfter" dataKey="cap" name="Daily cap" stroke="#f8fafc" dot={false} strokeWidth={1} isAnimationActive={false} />
        <Line type="stepAfter" dataKey="watchdog" name="Watchdog (120%)" stroke="#f87171" strokeDasharray="2 3" dot={false} isAnimationActive={false} />
      </ComposedChart>
    </ResponsiveContainer>
  </div>
);

const Month = ({ m, note }) => (
  <div className="mt-4 rounded-md border border-[#1e293b] p-3" data-testid="pacing-month">
    <div className="mb-1 flex items-center justify-between text-xs text-slate-400">
      <span className="flex items-center gap-1.5">Month to date
        <TooltipProvider><Tooltip><TooltipTrigger data-testid="pacing-month-info"><Info className="h-3.5 w-3.5" /></TooltipTrigger><TooltipContent className="max-w-xs text-xs">{note}</TooltipContent></Tooltip></TooltipProvider>
      </span>
      <span className="font-mono text-slate-200" data-testid="pacing-mtd">{fmtMoney(m.spend)} / {fmtMoney(m.budget)}</span>
    </div>
    <Bar value={m.overrun_note ? 0 : m.spend} max={m.budget} testid="pacing-mtd-bar" />
    {m.overrun_note && <div className="mt-1 text-[11px] text-amber-300" data-testid="pacing-overrun-note">{m.overrun_note} · info only, nothing is throttled on month-to-date</div>}
    <div className="mt-1 font-mono text-[11px] text-slate-500">Expected by now {fmtMoney(m.expected_by_now)} · projected month {fmtMoney(m.projected)} · day {m.day}/{m.days}</div>
  </div>
);

export const PacingPanel = () => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback((refresh) => {
    setBusy(true);
    return (refresh ? api.post("/pacing/refresh") : api.get("/pacing")).then((r) => { setD(r.data); setErr(""); if (refresh && r.data.note_refresh) toast.info(r.data.note_refresh); }).catch((e) => { setErr(errMsg(e)); if (refresh) toast.error(errMsg(e)); }).finally(() => setBusy(false));
  }, []);
  useEffect(() => { load(false); const t = setInterval(() => load(false), 30 * 60 * 1000); return () => clearInterval(t); }, [load]);
  if (err && !d) return <Panel title="Budget pacing · today" icon={Activity} testid="pacing-panel"><p className="text-sm text-slate-400">{err}</p></Panel>;
  if (!d) return <Panel title="Budget pacing · today" icon={Activity} testid="pacing-panel"><Spinner /></Panel>;
  const [tone, label] = PILL[d.status];
  return (
    <Panel title="Budget pacing · today (SGT)" icon={Activity} testid="pacing-panel" right={<div className="flex items-center gap-2"><Tag tone={tone} testid="pacing-status">{label}</Tag>
      <button data-testid="pacing-refresh" onClick={() => load(true)} disabled={busy} className="cc-btn cc-btn-ghost px-2 py-1 text-xs">{busy ? <Spinner className="h-3.5 w-3.5" /> : <RefreshCw className="h-3.5 w-3.5" />}</button></div>}>
      <div className="mb-3 flex flex-wrap gap-6 font-mono text-sm">
        <div><div className="cc-eyebrow">Spent today</div><div className="text-xl font-bold text-slate-50" data-testid="pacing-spend">{fmtMoney(d.spend_today)}</div></div>
        <div><div className="cc-eyebrow">Projected</div><div className="text-xl text-sky-300" data-testid="pacing-projected">{d.projected ? fmtMoney(d.projected) : "—"}</div></div>
        <div><div className="cc-eyebrow">Cap / watchdog</div><div className="text-xl text-slate-300">{fmtMoney(d.cap_daily)} / {fmtMoney(d.watchdog_limit)}</div></div>
      </div>
      <Chart points={d.points} />
      <div className="mt-4 space-y-2" data-testid="pacing-campaigns">{d.campaigns.filter((c) => c.status === "ENABLED" || c.spend > 0).map((c) => (
        <div key={c.id}><div className="flex items-center justify-between gap-3 text-xs"><span className="text-slate-300">{c.name}</span><span className="flex items-center gap-3">{c.status === "ENABLED" && <CampaignBudgetAction c={c} />}<span className="font-mono text-slate-400">{fmtMoney(c.spend)} / {fmtMoney(c.daily_budget)}</span></span></div><Bar value={c.spend} max={c.daily_budget} /></div>
      ))}</div>
      <Month m={d.month} note={d.note} />
      <div className="mt-2 font-mono text-[10px] text-slate-600">Updated {fmtDate(d.at)} · refreshed every 30 min (1 API query)</div>
    </Panel>
  );
};
